import { useState } from "react";
import { Link } from "react-router-dom";

import { BarList } from "../components/BarList";
import { Card, ErrorBox, Loading } from "../components/Page";
import { day, num } from "../lib/format";
import { useApi } from "../lib/useApi";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface Product {
  sku: string; name: string; flavour: string | null; size_ml: number | null; units_per_case: number | null;
  months: (number | null)[]; quarters: (number | null)[]; unplaced: number; total: number;
  per_full_quarter: number | null; bottles_per_full_quarter: number | null;
}
interface Unplaced { channel: string; channel_name: string; customer: string | null; invoice_no: string | null; date: string | null; cases: number }
interface Flavours {
  year: number; years: number[]; scope: string; as_of: string | null; us_as_of: string | null; months_with_data: number; full_quarters: number[];
  products: Product[]; unplaced: Unplaced[];
  totals: { months: (number | null)[]; quarters: (number | null)[]; unplaced: number | null; total: number | null;
            per_full_quarter: number | null; bottles_per_full_quarter: number | null };
}

const short = (name: string) => name.replace("Shipwreck ", "").replace(" Rum", "");
const cell = (v: number | null | undefined) => (v == null ? "" : v ? num(v) : "—");

/** One CSV of the month and quarter tables, for planning in a spreadsheet. */
function csv(d: Flavours): string {
  const months = MONTHS.slice(0, d.months_with_data);
  const head = ["Product", "SKU", ...months, "No month", ...["Q1", "Q2", "Q3", "Q4"], "Total", "Average full quarter", "Bottles a full quarter"];
  const line = (name: string, sku: string, r: Product | Flavours["totals"]) => [
    name, sku, ...r.months.slice(0, d.months_with_data).map((v) => v ?? ""), r.unplaced ?? "",
    ...r.quarters.map((v) => v ?? ""), r.total ?? "", r.per_full_quarter?.toFixed(1) ?? "", r.bottles_per_full_quarter?.toFixed(0) ?? ""];
  const rows = [head, ...d.products.map((p) => line(p.name, p.sku, p)), line("Total", "", d.totals)];
  return rows.map((r) => r.map((v) => (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : v)).join(",")).join("\n");
}

export function SalesFlavours() {
  const [scope, setScope] = useState("all");
  const [year, setYear] = useState<number | null>(null);
  const { data: d, error, reload } = useApi<Flavours>(`/sales/flavours?scope=${scope}${year ? `&year=${year}` : ""}`);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!d) return <Loading />;
  const months = MONTHS.slice(0, d.months_with_data);
  const full = d.full_quarters;
  const fullLabel = full.length ? `Q${full[0]}${full.length > 1 ? `–Q${full[full.length - 1]}` : ""} ${d.year}` : "";
  const download = () => {
    const url = URL.createObjectURL(new Blob([csv(d)], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `cases-by-flavour-${d.year}-${d.scope}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <p className="note muted" style={{ marginTop: 0 }}>
        Cases sold per product: each invoice's flavour counts from Zach's sales report{d.as_of ? <> of {day(d.as_of)}</> : null},
        and MHW's US detail{d.us_as_of ? <> of {day(d.us_as_of)}</> : null}. Money is not split by flavour outside the US, so this is cases.
      </p>
      <div className="flavour-tools">
        <span className="form-actions">
          <select aria-label="Channels" value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="all">All channels</option>
            <option value="us">USA (MHW)</option>
            <option value="non_us">Outside the USA</option>
          </select>
          {d.years.length > 1 && (
            <select aria-label="Year" value={d.year} onChange={(e) => setYear(Number(e.target.value))}>
              {d.years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
          <button className="link-btn" onClick={download}>Download CSV</button>
        </span>
      </div>

      {full.length > 0 ? (
        <Card title={`What a full quarter takes, on average (${fullLabel})`}>
          <BarList bars={d.products.filter((p) => p.per_full_quarter).map((p) => ({
            key: p.sku, label: short(p.name), value: p.per_full_quarter ?? 0,
            valueLabel: `${num(p.per_full_quarter)} cs`,
            href: `/products/${p.sku}`,
          }))} />
          <p className="note muted">
            The average of the {full.length} quarter{full.length > 1 ? "s" : ""} that {full.length > 1 ? "are" : "is"} over,{" "}
            {num(d.totals.per_full_quarter)} cases ({num(d.totals.bottles_per_full_quarter)} bottles) in all. The quarter still running is in the table below and is not
            averaged in. Only {d.year} has cases by flavour so far, so there is no last year to set against it.
          </p>
        </Card>
      ) : null}

      <Card title={`Quarter by quarter, ${d.year}`}>
        <div className="table-scroll">
          <table className="grid" data-testid="flavour-quarters">
            <thead><tr><th>Product</th>
              {[1, 2, 3, 4].map((q) => <th key={q} className="n">Q{q}{q * 3 > d.months_with_data && (q - 1) * 3 < d.months_with_data ? " so far" : ""}</th>)}
              <th className="n">No month</th><th className="n">Total</th><th className="n">Average full quarter</th><th className="n">Bottles</th></tr></thead>
            <tbody>
              {d.products.map((p) => (
                <tr key={p.sku}>
                  <td className="name"><Link to={`/products/${p.sku}`}>{short(p.name)}</Link></td>
                  {p.quarters.map((v, q) => <td key={q} className="n">{cell(v)}</td>)}
                  <td className="n">{cell(p.unplaced)}</td>
                  <td className="n"><strong>{num(p.total)}</strong></td>
                  <td className="n">{p.per_full_quarter == null ? "" : num(p.per_full_quarter)}</td>
                  <td className="n">{p.bottles_per_full_quarter == null ? "" : num(p.bottles_per_full_quarter)}</td>
                </tr>
              ))}
              <tr className="totals"><td>Total</td>
                {d.totals.quarters.map((v, q) => <td key={q} className="n">{cell(v)}</td>)}
                <td className="n">{cell(d.totals.unplaced)}</td>
                <td className="n"><strong>{num(d.totals.total)}</strong></td>
                <td className="n">{d.totals.per_full_quarter == null ? "" : num(d.totals.per_full_quarter)}</td>
                <td className="n">{d.totals.bottles_per_full_quarter == null ? "" : num(d.totals.bottles_per_full_quarter)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card title={`Month by month, ${d.year}`}>
        <div className="table-scroll">
          <table className="grid compact" data-testid="flavour-months">
            <thead><tr><th>Product</th>{months.map((m) => <th key={m} className="n">{m}</th>)}<th className="n">No month</th><th className="n">Total</th></tr></thead>
            <tbody>
              {d.products.map((p) => (
                <tr key={p.sku}>
                  <td className="name">{short(p.name)}</td>
                  {p.months.slice(0, d.months_with_data).map((v, i) => <td key={i} className="n">{cell(v)}</td>)}
                  <td className="n">{cell(p.unplaced)}</td>
                  <td className="n"><strong>{num(p.total)}</strong></td>
                </tr>
              ))}
              <tr className="totals"><td>Total</td>
                {d.totals.months.slice(0, d.months_with_data).map((v, i) => <td key={i} className="n">{cell(v)}</td>)}
                <td className="n">{cell(d.totals.unplaced)}</td>
                <td className="n"><strong>{num(d.totals.total)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {d.unplaced.length > 0 && (
        <Card title="Invoices that are not in a month">
          <p className="note muted" style={{ marginTop: 0 }}>
            Their date cell is blank, says something like "pending", or is a date in another year on the {d.year} sheet.
            They count in "No month" above; a date on the sheet puts them in their month.
          </p>
          <div className="table-scroll">
            <table className="grid compact" data-testid="flavour-unplaced">
              <thead><tr><th>Channel</th><th>Customer</th><th>Invoice</th><th>Date on the sheet</th><th className="n">Cases</th></tr></thead>
              <tbody>{d.unplaced.map((u) => (
                <tr key={`${u.channel}-${u.invoice_no}-${u.date}`}>
                  <td>{u.channel_name}</td><td>{u.customer ?? ""}</td>
                  <td>{u.invoice_no ? <Link to={`/sales/invoices/${u.channel}/${encodeURIComponent(u.invoice_no)}`}>{u.invoice_no.startsWith("row ") ? `no number (${u.invoice_no})` : `#${u.invoice_no}`}</Link> : ""}</td>
                  <td>{u.date ? day(u.date) : <span className="muted">none</span>}</td><td className="n">{num(u.cases)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}
